<template>
  <div class="upload-container">
    <!-- 已上传的文件 -->
    <div v-if="fileUrl" class="file-preview">
      <a :href="fileUrl" target="_blank" class="uploaded-file">{{ getFileNameFromUrl(fileUrl) }}</a>
      <div class="file-actions">
        <i class="el-icon-delete" @click="removeFile"></i>
      </div>
    </div>
    <!-- 上传按钮 -->
    <div v-else v-loading="uploading" class="upload-trigger" @click="triggerUpload">
      <i class="el-icon-plus upload-icon"></i>
      <div class="upload-text">点击上传</div>
    </div>
    <!-- 隐藏的文件输入框 -->
    <input
      type="file"
      ref="fileInput"
      @change="handleFileChange"
      :accept="acceptTypes.map(ext => '.'+ext).join(',')"
      style="display: none;"
    />
  </div>
</template>

<script>
export default {
  name: 'UploadFile',
  props: {
    value: {
      type: String,
      default: ''
    },
    disabled: {
      type: Boolean,
      default: false
    },
    maxSize: {
      type: Number,
      default: 100 // MB
    },
    acceptTypes: {
      type: Array,
      default: () => ['pdf', 'doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx', 'txt', 'zip', 'rar']
    }
  },
  data() {
    return {
      uploading: false,
      fileUrl: this.value
    };
  },
  watch: {
    value(newVal) {
      this.fileUrl = newVal;
    },
    fileUrl(newVal) {
      this.$emit('input', newVal);
    }
  },
  methods: {
    // 触发文件选择
    triggerUpload() {
      if (this.disabled || this.uploading) return;
      this.$refs.fileInput.click();
    },
    // 处理文件选择
    async handleFileChange(event) {
      const file = event.target.files[0];
      if (!file) return;
      // 校验类型
      const ext = file.name.split('.').pop().toLowerCase();
      if (!this.acceptTypes.includes(ext)) {
        this.$message.error(`只能上传 ${this.acceptTypes.join(', ')} 格式文件!`);
        this.$refs.fileInput.value = '';
        return;
      }
      // 校验大小
      if (file.size / 1024 / 1024 > this.maxSize) {
        this.$message.error(`文件大小不能超过 ${this.maxSize}MB!`);
        this.$refs.fileInput.value = '';
        return;
      }
      try {
        await this.uploadFile(file);
      } catch (error) {
        this.$message.error('上传失败，请重试');
      } finally {
        this.$refs.fileInput.value = '';
      }
    },
    // 上传文件
    async uploadFile(file) {
      this.uploading = true;
      try {
        const fileName = this.generateFileName(file.name);
        const fileUrl = await this.uploadS3File(file, fileName);
        this.fileUrl = fileUrl;
        this.$message.success('上传成功');
      } catch (error) {
        this.$message.error('上传失败');
      } finally {
        this.uploading = false;
      }
    },
    // S3上传
    async uploadS3File(file, fileName) {
      const { data: resData } = await this.$get('s3/config/info', {});
      if (resData.result !== 200) throw new Error('获取S3配置失败');
      const s3Config = resData.data;
      const accountid = "05e3a3acf3a5440a0f6aa4e94818fac2";
      const accessKeyId = "adfaeaebf3a0adc777a2365aa4f7500a";
      const accessKey = "93f858b5e3e7d01f34dc25fcd0e228c61124872996059c2ec464a0431e0355b4";
      const s3 = new AWS.S3({
        params: { Bucket: "static-taotan" },
        endpoint: `https://${accountid}.r2.cloudflarestorage.com`,
        accessKeyId: `${accessKeyId}`,
        secretAccessKey: `${accessKey}`,
        signatureVersion: "v4",
      });
      const albumPhotosKey = "micros/";
      const photoKey = albumPhotosKey + fileName;
      return new Promise((resolve, reject) => {
        s3.upload(
          {
            Key: photoKey,
            Body: file,
          },
          (error, data) => {
            if (error) {
              reject(error);
            } else {
              const domain = s3Config.domain;
              const fileUrl = `${domain}/${data.key}`;
              resolve(fileUrl);
            }
          }
        );
      });
    },
    // 生成文件名
    generateFileName(originalName) {
      const data = new Date();
      const year = data.getFullYear().toString();
      const month = (data.getMonth() + 1).toString().padStart(2, '0');
      const day = data.getDate().toString().padStart(2, '0');
      const hours = data.getHours().toString().padStart(2, '0');
      const minutes = data.getMinutes().toString().padStart(2, '0');
      const seconds = data.getSeconds().toString().padStart(2, '0');
      const ms = data.getMilliseconds().toString().padStart(3, '0');
      return `${year}${month}${day}${hours}${minutes}${seconds}${ms}.${originalName.split('.').pop()}`;
    },
    // 删除文件
    removeFile() {
      this.fileUrl = '';
    },
    // 提取文件名
    getFileNameFromUrl(url) {
      if (!url) return '';
      const parts = url.split('/');
      return parts[parts.length - 1] || '附件文件';
    }
  }
};
</script>

<style scoped>
.upload-container {
  display: inline-block;
  width: 200px;
  height: 40px;
  border: 1px dashed #d9d9d9;
  border-radius: 6px;
  cursor: pointer;
  position: relative;
  overflow: hidden;
}
.upload-container:hover {
  border-color: #409EFF;
}
.upload-trigger {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  align-items: center;
  background-color: #fafafa;
  padding-left: 10px;
}
.upload-icon {
  font-size: 20px;
  color: #8c939d;
  margin-right: 8px;
}
.upload-text {
  font-size: 14px;
  color: #8c939d;
}
.file-preview {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  position: relative;
}
.uploaded-file {
  color: #409EFF;
  text-decoration: underline;
  font-size: 14px;
  margin-right: 10px;
  max-width: 120px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  display: inline-block;
}
.file-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}
.file-actions i {
  color: #f56c6c;
  font-size: 18px;
  cursor: pointer;
}
.file-actions i:hover {
  color: #ff4949;
}
</style> 